import { useContext, useEffect, useState } from "react";
import { categorizeData, getIndexMetricDataPDF, getMetricGroupChartId, getSortedMetrics, getSvgMap, orgId } from "../utils/dashboardUtils";
import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import equitySvg from '../assets/Equity.svg';
import accessibilitySvg from '../assets/Accessibility.svg';
import livabilitySvg from '../assets/Livability.svg';
import educationSvg from '../assets/Education.svg';
import economySvg from '../assets/economy.svg';
import familySvg from '../assets/family.svg';

import MetricCategoriesContext from "../context/MetricCategoriesContext";
import PDFMetricCard from "../components/pdf/PDFMetricCard";
import LoadingIndicator from "../components/LoadingIndicator";
import TitleBanner from "../components/pdf/TitleBanner";
import DisaggregateMetricCard from "../components/DisaggregateMetricCard";
import ComparisonChart from "../components/charts/ComparisonChart";
import SingleBar from "../components/charts/SingleBar";
import { useCountyAgeData } from "../context/CountyAgeDataContext";
import SimpleDonutChart from "../components/charts/SimpleDonutChart";
import RaceEthnicityChart from "../components/charts/RaceEthnicityChart";
import SimpleBarChart from "../components/charts/SimpleBarChart";
import DoubleMetricCard from "../components/DoubleMetricCard";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    metadata: MetricMetadata[] | null;
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
    selectionActive: boolean;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, metadata, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, siteUrl, selectedArea, selectionActive }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const countyAgeData = useCountyAgeData();
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('data:', disaggregatedData);

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    // Get sorted metrics for each dashboard panel
    const sortedDemographicMetrics: DisaggregatedMetric[][] = getSortedMetrics('Demographics', categorizedData);
    const sortedAccessibilityMetrics: DisaggregatedMetric[][] = getSortedMetrics('Accessibility', categorizedData);
    const sortedChildFamilyMetrics: DisaggregatedMetric[][] = getSortedMetrics('Child & family', categorizedData);
    const sortedEducationMetrics: DisaggregatedMetric[][] = getSortedMetrics('Education', categorizedData);
    const sortedEconomyMetrics: DisaggregatedMetric[][] = getSortedMetrics('Economy', categorizedData);
    const sortedHousingMetrics: DisaggregatedMetric[][] = getSortedMetrics('Housing', categorizedData);

    // Get index metric data for each dashboard panel
    const indexMetricDataDemographic = getIndexMetricDataPDF('Demographics', metadata, disaggregatedData);
    const indexMetricDataAccessibility = getIndexMetricDataPDF('Accessibility', metadata, disaggregatedData);
    const indexMetricDataChildFamily = getIndexMetricDataPDF('Child & family', metadata, disaggregatedData);
    const indexMetricDataEducation = getIndexMetricDataPDF('Education', metadata, disaggregatedData);
    const indexMetricDataEconomy = getIndexMetricDataPDF('Economy', metadata, disaggregatedData);
    const indexMetricDataHousing = getIndexMetricDataPDF('Housing', metadata, disaggregatedData);

    // Define explicit metrics for PDF formatting
    const flatDemographicMetrics = sortedDemographicMetrics.flat();
    const singleFathers = sortedChildFamilyMetrics[1].find(metric => metric.id === 1192) || null;
    const singleMothers = sortedChildFamilyMetrics[1].find(metric => metric.id === 1193) || null;
    const bachelors = disaggregatedData.find(metric => metric.id === 220);
    const ged = disaggregatedData.find(metric => metric.id === 219);
    const nohs = disaggregatedData.find(metric => metric.id === 222);
    const ownerCostBurden = disaggregatedData.find(metric => metric.id === 232) || null;
    const renterCostBurden = disaggregatedData.find(metric => metric.id === 231) || null;
    

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[816px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    {/* Print and Close buttons */}
                    <div className="flex justify-center items-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-[#0A5F75] border border-[#0A5F75] text-white py-1 pl-7 pr-8 rounded-md"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-3 h-3 mr-2 mt-[0.5px]" />
                            <p className="text-xs font-bold">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-[#0A5F75] py-1 px-8 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-xs text-[#0A5F75] font-bold">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative">
                            <div className="w-full h-auto p-4 flex justify-between items-center bg-[#4D4D4D] rounded-md shadow-md">
                                {/* <img src={'laceyImg'} alt='Sacramento County Logo'/> */}
                                <div className="flex flex-col items-start justify-start mx-4 w-full">
                                    <p className="text-white font-bold text-[18px]">CAPC</p>
                                    <p className="text-xs text-white">{selectedArea || 'Sacramento County, CA'}</p>
                                    <p className="text-xs text-white font-bold">{siteUrl || ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="h-10 w-30 mr-8 mt-4" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        {/* Demographics Metrics */}
                        <div className="content">
                            <div className="shadow-md rounded-md mt-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographic Characteristics"/>
                                <p className="text-lacey-text mx-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {flatDemographicMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Demographics Charts */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographic Characteristics"/>
                                <div className="grid grid-cols-4 gap-2 place-items-center mx-4 mt-4">
                                    <div className="col-span-3 pr-4 pb-4 print:pr-0 w-full">
                                        {categorizedData.Demographics.filter(metric => [getMetricGroupChartId("Demographics", "race")].includes(metric.id))
                                        .map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                metric={metric}
                                                title={"Race & Ethnicity"}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showValue={false}
                                                showMetadataIcon={false}
                                                showYear={true}
                                            />
                                        ))}
                                        <RaceEthnicityChart indicators={disaggregatedData} title="Race and Ethnicity" tab="Demographics" chartName="race" height="h-96" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                                    </div>    
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 2: Child & Family */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataChildFamily} geoDetails={geoDetails} geoTag={geoTag} svg={familySvg} text="Child & Family"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedChildFamilyMetrics[0].map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}

                                    <div className="col-span-2 w-full pr-4 print:pr-0 pb-4">
                                        {categorizedData["Child & family"].filter(metric => 
                                            [getMetricGroupChartId("Child & family", "childrenage")].includes(metric.id) // Percent of Children by Age group
                                        ).map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                metric={metric}
                                                title={"Percentage of Children by Age Group"}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showValue={false}
                                                showMetadataIcon={false}
                                                showYear={true}
                                            />
                                        ))}
                                        <ComparisonChart indicators={disaggregatedData} metricId={1194} countyData={countyAgeData} geoTag={geoTag} geoDetails={geoDetails} title="Percent of Children by Age Group" height="h-72" />
                                    </div>

                                    <div className="col-span-2 w-full pr-4">
                                        <div className="bg-anti-flash print:bg-white pb-2">
                                            {categorizedData["Child & family"].filter(metric => 
                                                [getMetricGroupChartId("Child & family", "singleparent")].includes(metric.id) // Single Parent Families
                                            ).map(metric => (
                                                <DisaggregateMetricCard
                                                    key={metric.id}
                                                    id={metric.id}
                                                    metric={metric}
                                                    title={metric.name}
                                                    svg={getSvgMap(metric.id)}
                                                    disaggData={disaggregatedData}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    showValue={true}
                                                    showMetadataIcon={false}
                                                    showYear={true}
                                                />
                                            ))}
                                            
                                            {singleMothers && 
                                            <>
                                            <SingleBar
                                                key={singleMothers.id}
                                                metric={singleMothers}
                                                name="Single Mothers"
                                                width="w-full"
                                                svg={getSvgMap(singleMothers.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                            
                                            </>
                                            }
                                            {singleFathers && 
                                            <SingleBar
                                                key={singleFathers.id}
                                                metric={singleFathers}
                                                name='Single Fathers'
                                                width="w-full"
                                                svg={getSvgMap(singleFathers.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 3: Economy + Education */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEducation} geoDetails={geoDetails} geoTag={geoTag} svg={educationSvg} text="Education"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedEducationMetrics[0].map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}

                                    <div className="col-span-2 w-full pr-4 pb-4">
                                        <div className="bg-anti-flash print:bg-white pb-2">
                                            <DisaggregateMetricCard
                                                key={220}
                                                id={220}
                                                metric={disaggregatedData.find(indicator => indicator.id === 220) || null}
                                                title={"Educational Attainment"}
                                                svg={undefined}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                                showValue={false}
                                                showYear={true}
                                            />
                                            <div className="flex items-start justify-center ml-2">
                                                {nohs && 
                                                <div className="flex flex-col items-start mt-2">
                                                    <SimpleDonutChart indicators={disaggregatedData} id={nohs.id} fillColor="#00A18A" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                    
                                                    <p className="text-port-text text-xs font-bold mt-2 max-w-32">{nohs.name}</p>
                                                    
                                                    { nohs.versions[0]?.year && <p className="text-port-text text-xs max-w-32">in {nohs.versions[0]?.year}</p> }
                                                </div>
                                                }

                                                {ged && 
                                                <div className="flex flex-col items-start mt-2">
                                                    <SimpleDonutChart indicators={disaggregatedData} id={ged.id} fillColor="#00A18A" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                    
                                                    <p className="text-port-text text-xs font-bold mt-2 max-w-32">{ged.name}</p>

                                                    { ged.versions[0]?.year && <p className="text-port-text text-xs max-w-32">in {ged.versions[0]?.year}</p> }
                                                </div>
                                                }
                                                
                                                {bachelors && 
                                                <div className="flex flex-col items-start mt-2">
                                                    <SimpleDonutChart indicators={disaggregatedData} id={bachelors.id} fillColor="#00A18A" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                    
                                                    <p className="text-port-text text-xs font-bold mt-2 max-w-32">{bachelors.name}</p>

                                                    { bachelors.versions[0]?.year && <p className="text-port-text text-xs max-w-32">in {bachelors.versions[0]?.year}</p> }
                                                </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEconomy} geoDetails={geoDetails} geoTag={geoTag} svg={economySvg} text="Economy"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    <div className="col-span-2 w-full mb-4 pr-4">
                                        {categorizedData.Economy.filter(metric => 
                                            [getMetricGroupChartId("Economy", "hhinc")].includes(metric.id) // Annual Household Income
                                        ).map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                metric={metric}
                                                title={metric.name}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showValue={false}
                                                showMetadataIcon={false}
                                                showYear={true}
                                            />
                                        ))}
                                        <SimpleBarChart indicators={disaggregatedData} title="Annual Household Income" tab="Economy" chartName="hhinc" height="h-64" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                                    </div> 
                                    {sortedEconomyMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 4: Accessibility and Housing */}
                    <div className="page">
                        <div className="shadow-md rounded-md my-8">
                            <TitleBanner metric={indexMetricDataHousing} geoDetails={geoDetails} geoTag={geoTag} svg={livabilitySvg} text="Housing"/>
                            <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                {sortedHousingMetrics[0].map((metric) => (
                                    <div className="mt-2">
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    </div>
                                ))}

                                <div className="col-span-2 mx-auto">
                                    {renterCostBurden && ownerCostBurden && (
                                    <DoubleMetricCard 
                                        key={231}
                                        metricOne={renterCostBurden}
                                        metricTwo={ownerCostBurden}
                                        title="Share of Households that are Cost-Burdened"
                                        metadataId={231}
                                        width="w-full"
                                        svg={getSvgMap(231)}
                                        disaggData={disaggregatedData}
                                        geoDetails={geoDetails}
                                        geoTag={geoTag}
                                        selectionActive={selectionActive}
                                        showMetadataIcon={false}
                                    />
                                    )}
                                </div>

                                {sortedHousingMetrics[1].map((metric) => (
                                    <PDFMetricCard
                                    key={metric.id}
                                    metric={metric}
                                    width="w-full"
                                    svg={getSvgMap(metric.id)}
                                    showMetadataIcon={false}
                                    disaggData={disaggregatedData}
                                    />
                                ))}
                            </div>
                        </div>
                        {/* Accessibility */}
                        <div className="shadow-md rounded-md my-8">
                            <TitleBanner metric={indexMetricDataAccessibility} geoDetails={geoDetails} geoTag={geoTag} svg={accessibilitySvg} text="Accessiblity"/>
                            <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                <div className="col-span-2 grid grid-cols-2 gap-4 mb-4">
                                    {sortedAccessibilityMetrics[0].map(metric => (
                                    <div key={metric.id}>
                                        <div className="flex items-start justify-center">
                                            {metric && 
                                            <div className="flex flex-col items-start">
                                                <SimpleDonutChart indicators={disaggregatedData} id={metric.id} fillColor="#00A18A" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                
                                                <p className="text-port-text text-xs font-bold mt-2 max-w-32">{metric.name}</p>

                                                { metric.versions[0]?.unit_descriptor && <p className="text-port-text text-xs max-w-32">{metric.versions[0]?.unit_descriptor}</p> }

                                                { metric.versions[0]?.year && <p className="text-port-text text-xs max-w-32">in {metric.versions[0]?.year}</p> }
                                            </div>
                                            }
                                        </div>
                                    </div>
                                    ))}   
                                </div>

                                <div className="col-span-2 w-full mb-4 pr-4">
                                    {categorizedData.Accessibility.filter(metric => 
                                        [getMetricGroupChartId("Accessibility", "commute")].includes(metric.id) // Commute Mode
                                    ).map(metric => (
                                        <DisaggregateMetricCard
                                            key={metric.id}
                                            id={metric.id}
                                            metric={metric}
                                            title={metric.name}
                                            svg={getSvgMap(metric.id)}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            showValue={false}
                                            showMetadataIcon={false}
                                            showYear={true}
                                        />
                                    ))}
                                    <SimpleBarChart indicators={disaggregatedData} title="Commute Mode" tab="Accessibility" chartName="commute" height="h-56" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                                </div>  
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;